import {Time} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerValue,
  Hub,
  StickerSettingsInBoard,
  Tag,
  TimerStickerValue
} from 'yougile-model'
import {Sync} from '@client/sync'
import {noDefaultState} from '@components/sticker/my-stickers/board-stickers/sticker-settings/use-common-handlers'
import {Sticker} from '@extensions/sticker/sticker'
import {RequiredSticker} from './types'

const getDefaultState = ({
  hasDefaultState,
  defaultState
}: StickerSettingsInBoard): string | undefined => {
  if (!hasDefaultState || !defaultState || defaultState === noDefaultState) {
    return undefined
  }
  return defaultState as string
}

interface Props {
  board: Hub
  templateStickers?: {
    // sticker value
    [stickerId: DataObjectId]: any
  }
}

export const getRequiredStickers = (props: Props): RequiredSticker[] => {
  const {board, templateStickers = {}} = props

  const stickersSettings = board?.data?.stickersSettings || {}

  const box = Sync.getCurrentSandbox()

  const boardStickers = (
    Object.keys(stickersSettings) as DataObjectId[]
  ).reduce((acc, stickerId) => {
    if (!board.stickers[stickerId]) {
      return acc
    }

    const settings = stickersSettings[stickerId]
    if (!settings) {
      return acc
    }

    if (settings.required !== 'offer' && settings.required !== 'require') {
      return acc
    }

    const tag = box.get(stickerId) as Tag
    if (Sticker.check(tag)) {
      acc.push({
        required: settings.required,
        tag,
        defaultState: getDefaultState(settings)
      })
    }
    return acc
  }, [] as RequiredSticker[])

  const templateStickersRes = (
    Object.keys(templateStickers) as DataObjectId[]
  ).reduce((acc, stickerId) => {
    if (!board.stickers[stickerId]) {
      return acc
    }

    const value = templateStickers[stickerId]

    const tag = Sticker.getById(box, stickerId)
    if (tag.title === 'Deadline') {
      const deadlineValue = (value as DeadlineStickerValue).current
      if (value) {
        const now = Time.now()

        const deadlineStart = Time.startOfDay(deadlineValue.timestamp)
        const nowStart = Time.startOfDay(now)

        const shift =
          Time.day * Math.floor((nowStart - deadlineStart) / Time.day)

        updateStickerDates(value, shift)
      }
    }

    if (tag.title === 'Timer') {
      const timerValue = (value as TimerStickerValue)?.current
      if (timerValue) {
        timerValue.timestamp = Time.now()
      }
    }

    if (Sticker.check(tag)) {
      const boardStickerData = boardStickers.find(bs => bs.tag.id === stickerId)
      acc.push({
        required: boardStickerData?.required || 'offer',
        tag,
        defaultState: value
      })
    }
    return acc
  }, [] as RequiredSticker[])

  boardStickers.forEach(boardSticker => {
    const templateSticker = templateStickersRes.find(
      templateSticker => templateSticker.tag.id === boardSticker.tag.id
    )

    if (!templateSticker) {
      templateStickersRes.push(boardSticker)
    }
  })

  return templateStickersRes
}

export const updateStickerDates = (
  val: DeadlineStickerValue,
  shift: number
): void => {
  if (val.current.deadline) {
    val.current.deadline += shift
  }
  if (val.current.timestamp) {
    val.current.timestamp += shift
  }
  if (val.current.startDate) {
    val.current.startDate += shift
  }
}
